/**
 * F-148 §4.7 scan bar (AC31). Wraps the unchanged `BarcodeScanInput` (keyboard wedge + camera
 * dialog). Each submit becomes a FIFO capture; lookups use the injected F045 contract; results go
 * to the draft reducer, which returns the outcome as `feedback`. One polite live region announces
 * every outcome except `ignored_repeat`. Scanning never calls a stock RPC.
 *
 * Camera vs keyboard: the camera path of `BarcodeScanInput` calls `onChange` and `onSubmit`
 * synchronously in one call; keyboard/wedge entries change the value in earlier events and submit
 * on Enter. A submit whose value was set by `onChange` in the same task is therefore camera-sourced
 * and eligible for the 1500 ms repeat dedupe; keyboard entries never dedupe.
 *
 * Queue lifetime: the queue is owned by the mount effect, not by render. Each effect setup creates a
 * fresh queue and each cleanup disposes exactly that queue. A disposed queue is permanent
 * (`scan-queue.ts` never reactivates it), so a render-scoped queue would die on React StrictMode's
 * setup→cleanup→setup replay and every later submit would be silently dropped. Handlers always
 * resolve the live queue through a ref; results from a disposed queue never reach component state.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { BarcodeLookupResult } from '@dukanti/shared';
import {
  RECEIVING_LIMITS,
  type ReceivingScanLookup,
  type ReceivingScanOutcome,
} from '@dukanti/shared/admin';
import { BarcodeScanInput, type BarcodeScanInputHandle } from '@/features/stock/components';
import { cn } from '@/lib/utils';
import type { ScanFeedback } from '../lib/draft-reducer';
import { withRawValues } from '../lib/error-messages';
import {
  createScanQueue,
  type ScanCapture,
  type ScanQueue,
  type ScanSource,
} from '../lib/scan-queue';

export interface ReceivingScanBarProps {
  readonly lookup: (rawBarcode: string) => Promise<BarcodeLookupResult>;
  readonly onLookupResult: (lookup: ReceivingScanLookup, captureId: string) => void;
  readonly feedback: ScanFeedback | null;
  readonly disabled?: boolean;
  readonly className?: string;
}

const FAILURE_KINDS: ReadonlySet<ReceivingScanOutcome['kind']> = new Set([
  'pack_line',
  'duplicate_lines',
  'already_unresolved',
  'invalid',
  'line_limit',
  'quantity_limit',
  'lookup_failed',
  'unresolved_added',
]);

export function ReceivingScanBar({
  lookup,
  onLookupResult,
  feedback,
  disabled = false,
  className,
}: ReceivingScanBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<BarcodeScanInputHandle | null>(null);
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{ text: string; failure: boolean } | null>(null);
  const cameraCandidateRef = useRef<string | null>(null);
  const lookupRef = useRef(lookup);
  const onResultRef = useRef(onLookupResult);
  lookupRef.current = lookup;
  onResultRef.current = onLookupResult;
  const queueRef = useRef<ScanQueue | null>(null);

  // Returns the live queue, creating one if none is active. The queue reads `lookupRef` and
  // `onResultRef` at call time, so it never needs recreating when props change; sequential
  // FIFO capture semantics live inside the queue.
  const ensureQueue = useCallback((): ScanQueue => {
    const active = queueRef.current;
    if (active) return active;
    const created = createScanQueue({
      lookup: (raw: string) => lookupRef.current(raw),
      onResult: (capture: ScanCapture, result: ReceivingScanLookup) => {
        onResultRef.current(result, capture.captureId);
        if (result.status !== 'lookup_failed') {
          setValue((current) => (current === capture.rawBarcode ? '' : current));
        }
        inputRef.current?.focus();
      },
      onPendingChange: setPending,
    });
    queueRef.current = created;
    return created;
  }, []);

  // Effect-owned lifetime: setup activates a queue, cleanup disposes that same queue and clears
  // the ref so a StrictMode replay (or a real remount) gets a fresh one. `dispose()` drops
  // waiting captures and makes the queue ignore its in-flight completion, so no callback or
  // state update can fire after cleanup.
  useEffect(() => {
    const queue = ensureQueue();
    return () => {
      queue.dispose();
      if (queueRef.current === queue) queueRef.current = null;
    };
  }, [ensureQueue]);

  const handleChange = useCallback((next: string): void => {
    setValue(next);
    cameraCandidateRef.current = next;
    queueMicrotask(() => {
      if (cameraCandidateRef.current === next) cameraCandidateRef.current = null;
    });
  }, []);

  const handleSubmit = useCallback(
    (raw: string): void => {
      if (disabled) return;
      const source: ScanSource = cameraCandidateRef.current === raw ? 'camera' : 'keyboard';
      cameraCandidateRef.current = null;
      ensureQueue().enqueue(raw, source);
    },
    [disabled, ensureQueue],
  );

  const announcementFor = useCallback(
    (outcome: ReceivingScanOutcome): string | null => {
      switch (outcome.kind) {
        case 'added':
          return t(
            'admin.receiving.scan.added',
            withRawValues({
              product: outcome.productName,
              variant: outcome.optionSummary,
              expected: outcome.expectedUnits,
              accepted: outcome.expectedUnits,
            }),
          );
        case 'incremented':
          return t(
            'admin.receiving.scan.incremented',
            withRawValues({
              product: outcome.productName,
              variant: outcome.optionSummary,
              expected: outcome.expectedUnits,
              accepted: outcome.acceptedUnits,
            }),
          );
        case 'pack_line':
          return t('admin.receiving.scan.packLine', { line: outcome.lineNo });
        case 'duplicate_lines':
          return t('admin.receiving.scan.duplicateLines', { lines: outcome.lineNos.join(', ') });
        case 'unresolved_added':
          return outcome.reason === 'ineligible'
            ? t('admin.receiving.scan.unresolvedIneligible', { line: outcome.lineNo })
            : t('admin.receiving.scan.unresolvedUnknown', { line: outcome.lineNo });
        case 'already_unresolved':
          return t('admin.receiving.scan.alreadyUnresolved', { line: outcome.lineNo });
        case 'invalid':
          return t('admin.receiving.scan.invalid');
        case 'line_limit':
          return t('admin.receiving.scan.lineLimit', { max: RECEIVING_LIMITS.maxLines });
        case 'quantity_limit':
          return t('admin.receiving.scan.quantityLimit', { line: outcome.lineNo });
        case 'lookup_failed':
          return t('admin.receiving.scan.lookupFailed');
        case 'ignored_repeat':
          return null;
      }
    },
    [t],
  );

  // Re-announce identical text: clear the region, then set it on the next frame.
  useEffect(() => {
    if (!feedback) return;
    const text = announcementFor(feedback.outcome);
    if (text === null) return;
    setMessage(null);
    const failure = FAILURE_KINDS.has(feedback.outcome.kind);
    let frame = 0;
    const raf = globalThis.requestAnimationFrame;
    if (typeof raf === 'function') {
      frame = raf(() => setMessage({ text, failure }));
      return () => globalThis.cancelAnimationFrame(frame);
    }
    const handle = setTimeout(() => setMessage({ text, failure }), 0);
    return () => clearTimeout(handle);
    // Only a new feedback seq should re-announce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback?.seq]);

  return (
    <section
      className={cn('space-y-2', className)}
      aria-label={t('admin.receiving.scan.label')}
      data-testid="receiving-scan-bar"
    >
      <h2 className="text-foreground text-start text-sm font-semibold">
        {t('admin.receiving.scan.label')}
      </h2>
      <BarcodeScanInput
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onSubmit={handleSubmit}
        disabled={disabled}
      />
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={t('admin.receiving.scan.liveRegion')}
        className={cn(
          'flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2 text-start text-sm',
          message?.failure
            ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100'
            : message
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-100'
              : 'border-border bg-card text-muted-foreground',
        )}
        data-testid="scan-live-region"
        data-outcome={feedback?.outcome.kind ?? ''}
      >
        {pending ? (
          <Loader2
            aria-hidden="true"
            className="size-4 shrink-0 animate-spin motion-reduce:animate-none"
          />
        ) : message ? (
          message.failure ? (
            <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
          ) : (
            <CheckCircle2 aria-hidden="true" className="size-4 shrink-0" />
          )
        ) : null}
        <span>{message ? <bdi>{message.text}</bdi> : pending ? t('common.loading') : ''}</span>
      </div>
    </section>
  );
}
